import React, { useEffect, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import { api, STAGES, stageMeta } from "../lib/api";
import { PageHeader, Spinner, Stat } from "../components/UI";

const STAGE_COLORS = {
  lead: "#94A3B8",
  qualified: "#EAB308",
  demo: "#F97316",
  proposal: "#6366F1",
  closed: "#16A34A",
  lost: "#DC2626",
};

// Static recharts config — extracted to module scope so the same object
// identity is used on every render (otherwise React/recharts re-mount
// chart internals each time, which is wasteful).
const CHART_MARGIN = { top: 8, right: 8, left: -16, bottom: 8 };
const AXIS_TICK = { fill: "#737373", fontSize: 11 };
const TOOLTIP_CURSOR = { fill: "rgba(209,167,126,0.1)" };
const BAR_RADIUS = [8, 8, 0, 0];

export default function Analytics() {
  const [pipeline, setPipeline] = useState(null);
  const [weekly, setWeekly] = useState(null);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [p, w, b] = await Promise.all([
          api.get("/analytics/pipeline"),
          api.get("/analytics/weekly"),
          api.get("/analytics/brands"),
        ]);
        setPipeline(p.data);
        setWeekly(w.data);
        setBrands(b.data.items || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <Spinner />;

  const pipelineData = (pipeline?.stages || []).map((s) => ({
    stage: stageMeta(s.stage).label,
    count: s.count,
    color: STAGE_COLORS[s.stage],
  }));

  return (
    <div>
      <PageHeader
        testid="analytics-header"
        title="Analytics"
        subtitle="Read the room. Sharpen the pitch."
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <Stat label="Total accounts" value={pipeline?.total ?? 0} />
        <Stat label="Closed" value={pipeline?.closed ?? 0} hint={`${pipeline?.conversion_rate ?? 0}% conversion`} accent="text-stage-closedFg" />
        <Stat label="Visits this week" value={weekly?.this_week_total ?? 0} />
        <Stat
          label="WoW change"
          value={`${weekly?.delta >= 0 ? "+" : ""}${weekly?.delta ?? 0}`}
          hint={weekly?.delta_pct != null ? `${weekly.delta_pct}%` : ""}
          accent={(weekly?.delta ?? 0) >= 0 ? "text-stage-closedFg" : "text-stage-lostFg"}
        />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="card p-6">
          <div className="label">Pipeline</div>
          <h3 className="font-display text-2xl text-brand-ink mb-4">Stage distribution</h3>
          <div className="h-72" data-testid="chart-pipeline">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={pipelineData} margin={CHART_MARGIN}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E8E4D9" />
                <XAxis dataKey="stage" tick={AXIS_TICK} />
                <YAxis allowDecimals={false} tick={AXIS_TICK} />
                <Tooltip cursor={TOOLTIP_CURSOR} />
                <Bar dataKey="count" radius={BAR_RADIUS}>
                  {pipelineData.map((d) => (
                    <Cell key={d.stage} fill={d.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card p-6">
          <div className="label">Activity</div>
          <h3 className="font-display text-2xl text-brand-ink mb-4">This week's visits</h3>
          <div className="h-72" data-testid="chart-weekly">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={weekly?.daily || []} margin={CHART_MARGIN}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E8E4D9" />
                <XAxis dataKey="day" tick={AXIS_TICK} />
                <YAxis allowDecimals={false} tick={AXIS_TICK} />
                <Tooltip cursor={TOOLTIP_CURSOR} />
                <Bar dataKey="count" fill="#1A1A1A" radius={BAR_RADIUS} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="card p-6 mt-4">
        <div className="label">Brand performance</div>
        <h3 className="font-display text-2xl text-brand-ink mb-4">Per-brand traction</h3>
        {brands.length === 0 ? (
          <div className="text-brand-mutedFg text-sm">No data yet. Tag your brands on establishment cards.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="brand-performance-table">
              <thead>
                <tr className="text-left text-brand-mutedFg border-b border-brand-border">
                  <th className="py-2 pr-4">Brand</th>
                  <th className="py-2 pr-4">Accounts</th>
                  <th className="py-2 pr-4">Closed</th>
                  <th className="py-2 pr-4">Success rate</th>
                </tr>
              </thead>
              <tbody>
                {brands.map((b) => (
                  <tr key={b.brand_id} className="border-b border-brand-border last:border-0">
                    <td className="py-3 pr-4 font-medium text-brand-ink">{b.brand_name}</td>
                    <td className="py-3 pr-4">{b.establishments}</td>
                    <td className="py-3 pr-4">{b.closed}</td>
                    <td className="py-3 pr-4">
                      <div className="flex items-center gap-2">
                        <div className="w-28 h-1.5 bg-brand-muted rounded-full overflow-hidden">
                          <div className="h-full bg-brand-ink" style={{ width: `${Math.min(100, b.success_rate)}%` }} />
                        </div>
                        <span className="text-brand-ink2">{b.success_rate}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
